import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, QrCode, Car, User, Clock, AlertCircle, CheckCircle, Loader, MapPin } from 'lucide-react';
import QRScannerModal from './QRScannerModal';
import { vehiclesAPI, handleApiError } from '../services/api';
import { useFlashMessage } from './FlashMessage';
import { useAuth } from '../context/AuthContext';

const DeliveryDialog = ({ isOpen, onClose, onDeliverySuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showScanner, setShowScanner] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [newAmount, setNewAmount] = useState('');
  const [parkingSpot, setParkingSpot] = useState('');
  const { showSuccess, showDanger } = useFlashMessage();

  // Extract location prefix from location name
  // Example: "Cevahir Sahne" → "CS", "Akasya Junction" → "AJ"
  const getLocationPrefix = () => {
    if (!user?.locations || user.locations.length === 0) {
      return '';
    }

    // Get first active location
    const activeLocation = user.locations[0];
    if (!activeLocation?.name) {
      return '';
    }

    // Turkish to English character mapping
    const turkishToEnglish = {
      'Ç': 'C', 'ç': 'c',
      'Ğ': 'G', 'ğ': 'g',
      'İ': 'I', 'ı': 'i',
      'Ö': 'O', 'ö': 'o',
      'Ş': 'S', 'ş': 's',
      'Ü': 'U', 'ü': 'u'
    };

    // Replace Turkish characters with English equivalents
    const normalizedName = activeLocation.name
      .split('')
      .map(char => turkishToEnglish[char] || char)
      .join('');

    // Split by space and get first letter of each word, make uppercase
    const words = normalizedName.trim().split(/\s+/);
    const prefix = words.map(word => word.charAt(0).toUpperCase()).join('');

    console.log('Location prefix extracted:', activeLocation.name, '→', prefix);
    return prefix;
  };

  // Modal kapandığında state'i temizle
  useEffect(() => {
    if (!isOpen) {
      setVehicleData(null);
      setNewAmount('');
      setParkingSpot('');
      setShowScanner(false);
    } else {
      // Modal açıldığında direkt QR tarayıcıyı aç
      setShowScanner(true);
    }
  }, [isOpen]);

  const handleQRScan = async (qrData) => {
    console.log('handleQRScan başladı, qrData:', qrData);
    setLoading(true);
    // QR modal'ı burada kapatma - başarılı scan sonrasında kapatacağız

    try {
      // QR kod ile araç bilgilerini getir
      let response;
      let searchValue = qrData.trim();

      // UUID formatı kontrolü (QR token)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchValue);

      // Sadece sayılardan oluşuyor mu? (Örn: "1", "2", "23")
      const isNumericOnly = /^\d+$/.test(searchValue);

      // Referans numarası formatı kontrolü (Örn: CS1, AJ2)
      const isReference = /^[A-Z]+\d+$/i.test(searchValue);

      // Eğer sadece sayı girildiyse, location prefix'i otomatik ekle
      if (isNumericOnly && !isUUID) {
        const locationPrefix = getLocationPrefix();
        if (locationPrefix) {
          searchValue = locationPrefix + searchValue;
          console.log('Location prefix eklendi:', qrData, '→', searchValue);
        }
      }

      if (isUUID) {
        // QR token ile ara
        response = await vehiclesAPI.getVehicleByQR(searchValue);
      } else if (isReference || (isNumericOnly && getLocationPrefix())) {
        // Referans numarası ile ara (prefix otomatik eklendiyse de buraya düşer)
        response = await vehiclesAPI.searchByReference(searchValue.toUpperCase());
      } else if (isNumericOnly) {
        // Transaction ID ile ara (prefix yoksa sadece sayı olarak ara)
        response = await vehiclesAPI.searchByTransactionId(searchValue);
      } else {
        // Plaka numarası ile ara
        response = await vehiclesAPI.searchByPlate(searchValue);
      }

      if (response.data.success && response.data.data) {
        const vehicle = response.data.data;
        console.log('Araç bulundu:', vehicle);

        // QR modal ve delivery dialog'u kapat
        setShowScanner(false);
        onClose();

        // Status sayfasına yönlendir
        console.log('Status sayfasına yönlendiriliyor:', `/vehicles/status/${vehicle.id}`);
        showSuccess(`${vehicle.plate_number} plakalı araç bulundu! Durum güncelleme sayfasına yönlendiriliyorsunuz...`);
        navigate(`/vehicles/status/${vehicle.id}`);
      } else {
        console.log('Araç bulunamadı');
        showDanger('Araç bulunamadı veya teslim edilebilir durumda değil.');
        // Araç bulunamadı - QR modal'ını kapat ama delivery dialog'u da kapat
        setShowScanner(false);
      }
    } catch (error) {
      console.error('Vehicle lookup error:', error);
      showDanger('Araç arama hatası: ' + handleApiError(error));
      // Hata durumunda QR modal'ını kapat
      setShowScanner(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelivery = async () => {
    if (!vehicleData) return;

    setDeliveryLoading(true);

    try {
      // Tutar güncelleme ve durum değişikliği
      const updateData = {
        status: 'delivered',
        actual_amount: parseFloat(newAmount) || 0,
        delivered_at: new Date().toISOString()
      };

      const response = await vehiclesAPI.updateVehicleStatus(vehicleData.id, updateData);

      if (response.data.success) {
        showSuccess(`${vehicleData.plate_number} plakalı araç başarıyla teslim edildi.`);
        onDeliverySuccess();
        handleClose();
      } else {
        showDanger('Teslim işlemi başarısız: ' + response.data.message);
      }
    } catch (error) {
      console.error('Delivery error:', error);
      showDanger('Teslim işlemi hatası: ' + handleApiError(error));
    } finally {
      setDeliveryLoading(false);
    }
  };

  const handleClose = () => {
    setVehicleData(null);
    setNewAmount('');
    setShowScanner(false);
    onClose();
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  const getServiceTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale Hizmeti';
      default: return type;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {!showScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[50] p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-[#7a2c2c] to-[#5a1f1f] text-white px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
            <h3 className="text-lg font-semibold">Araç Teslimi</h3>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable content area */}
          <div className="overflow-y-auto flex-1 p-6">

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 animate-spin text-[#7a2c2c]" />
              <span className="ml-2 text-gray-600">Araç bilgileri alınıyor...</span>
            </div>
          )}

          {!vehicleData && !loading && !showScanner && (
            <div className="text-center py-8">
              <div className="mb-4">
                <QrCode className="w-16 h-16 mx-auto text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                QR Kod Tarayın
              </h4>
              <p className="text-gray-600 mb-6">
                Teslim edilecek aracın QR kodunu tarayın veya plaka numarasını manuel olarak girin
              </p>
              <button
                onClick={() => setShowScanner(true)}
                className="btn-primary flex items-center justify-center mx-auto"
              >
                <QrCode className="w-5 h-5 mr-2" />
                QR Kod Tara
              </button>
            </div>
          )}

          {vehicleData && (
            <div className="space-y-6">
              {/* Araç Bilgileri */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <Car className="w-5 h-5 mr-2" />
                  Araç Bilgileri
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plaka:</span>
                    <span className="font-medium">{vehicleData.plate_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Müşteri:</span>
                    <span>{vehicleData.customer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Telefon:</span>
                    <span>{vehicleData.customer_phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Hizmet:</span>
                    <span>{getServiceTypeText(vehicleData.service_type)}</span>
                  </div>
                  {vehicleData.check_in && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {vehicleData.transaction_type === 'historical' ? 'Son Giriş:' : 'Giriş:'}
                      </span>
                      <span>{formatDateTime(vehicleData.check_in)}</span>
                    </div>
                  )}
                  {vehicleData.transaction_type === 'historical' && (
                    <div className="text-xs text-orange-600 font-medium">
                      * Geçmiş kayıt - Yeni işlem başlatılacak
                    </div>
                  )}
                </div>
              </div>

              {/* Tutar Güncelleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Güncel Tutar (₺)
                </label>
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  className="form-input w-full"
                  placeholder="0"
                  min="0"
                  step="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Orijinal tutar: ₺{parseFloat(vehicleData.actual_amount || 0).toFixed(0)}
                </p>
              </div>


              {/* Park Yeri */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Park Yeri (Opsiyonel)
                </label>
                <input
                  type="text"
                  value={parkingSpot}
                  onChange={(e) => setParkingSpot(e.target.value)}
                  className="form-input w-full"
                  placeholder="Örn: A-15, Kat 2, Blok C-12"
                  maxLength={50}
                />
              </div>

              {/* Uyarı */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-yellow-700 font-medium">Dikkat!</p>
                    <p className="text-yellow-600">
                      Teslim işlemi tamamlandıktan sonra araç durumu "Teslim Edildi" olarak işaretlenecek ve işlem sonlanacaktır.
                    </p>
                  </div>
                </div>
              </div>

              {/* Aksiyon Butonları */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleDelivery}
                  disabled={deliveryLoading}
                  className="btn-primary flex items-center justify-center disabled:opacity-50"
                >
                  {deliveryLoading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Teslim Ediliyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Teslim Et
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setVehicleData(null);
                    setNewAmount('');
                    setParkingSpot('');
                    setShowScanner(true);
                  }}
                  className="btn-secondary flex items-center justify-center"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Başka Araç Tara
                </button>
                <button
                  onClick={handleClose}
                  className="btn-secondary"
                >
                  İptal
                </button>
              </div>
            </div>
          )}
          </div>
          </div>
        </div>
      )}

      {/* QR Scanner Modal */}
      <QRScannerModal
        isOpen={showScanner}
        onClose={() => {
          console.log('QR modal X butonuna basıldı - ana dialog da kapatılıyor');
          setShowScanner(false);
          // X butonuna basıldığında ana dialog'u da kapat
          onClose();
        }}
        onScanSuccess={handleQRScan}
        title="Teslim Edilecek Aracı Tara"
      />
    </>
  );
};

export default DeliveryDialog;